/// <amd-module name="Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/FirstRunExperiencePanel" />
import * as React from "react";
import { localeFormat, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { VssComponent, IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { ContentSize } from "VSSUI/Callout";
import { IWorkItemService } from "Wit/WorkItem/Service/Contracts";
import { ExternalConnection } from "Wit/Clients/Internal/Contracts/WorkItemTracking";
import { WorkItem } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { GitHubConnectionConstants } from "Wit/Settings/BoardsExternalIntegration/constants/Constants";
import { BasePanel } from "Wit/Settings/BoardsExternalIntegration/components/panels/BasePanel";
import {
    IBoardInfo,
    IFirstRunExperiencePanelProps
} from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/FirstRunExperiencePanel.Props";
import { FirstRunExperienceCreateGitHubPullRequestStep } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceCreateGitHubPullRequestStep";
import { FirstRunExperienceCreateWorkItemStep } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/Steps/FirstRunExperienceCreateWorkItemStep";
import { FirstRunExperiencePanelStaticContent } from "Wit/Settings/BoardsExternalIntegration/components/panels/FirstRunExperience/FirstRunExperiencePanelStaticContent";
import { publishGitHubIntegrationTelemetry, GitHubIntegrationCIActions } from "Wit/Settings/BoardsExternalIntegration/telemetry/GitHub";
import * as Resources from "Wit/Settings/BoardsExternalIntegration/Resources";

const TotalFirstRunStepsCount: number = 2;
const WorkItemServiceId: string = "work-item-service";
const FirstRunExperiencePanelImage = "ms.vss-work-web/boards-external-integration-content/assets/GitHub_App_FRE_Panel_Image.svg";

export interface IFirstRunExperiencePanelState {
    showInteractiveContent: boolean;
    activeStepId: number;
    isStepProcessing: boolean;
    isCompleted: boolean;
    stepErrorMessage?: string;
}

export class FirstRunExperiencePanel extends VssComponent<IFirstRunExperiencePanelProps, IFirstRunExperiencePanelState> {
    private _targetWorkItem: WorkItem;
    private _panelImageSource: string;
    private _createGitHubPullRequestStepElapsedTime: number;
    private _startTimeStamp: number;
    private _completeTimeStamp: number;
    private _repoNames: string[];

    constructor(props: IFirstRunExperiencePanelProps, context: IVssComponentContext) {
        super(props, context);

        this._panelImageSource = getAssetLocation(this.context.pageContext, FirstRunExperiencePanelImage);

        this._repoNames = [];
        const { connection } = this.props;
        if (connection && connection.externalGitRepos) {
            this._repoNames = connection.externalGitRepos
                .map(repo => repo.name)
                .sort((name1: string, name2: string) => localeIgnoreCaseComparer(name1, name2));
        }

        this.state = {
            showInteractiveContent: this._shouldShowInteractiveContent(),
            activeStepId: 1,
            isStepProcessing: false,
            isCompleted: false
        };
    }

    public componentDidMount(): void {
        this._startTimeStamp = Date.now();
    }

    public render(): JSX.Element | undefined {
        const { showInteractiveContent } = this.state;
        return (
            <BasePanel
                className="boards-external-integration-first-run-experience-panel"
                title={Resources.FirstRunExperience_Title}
                onRenderHeaderContent={this._renderHeader}
                onRenderPanelFooter={this._renderFooter}
                onDismiss={this._onDismiss}
                size={ContentSize.Medium}
                showCloseButton={false}
            >
                {showInteractiveContent ? this._renderInteractiveContent() : <FirstRunExperiencePanelStaticContent />}
            </BasePanel>
        );
    }

    private readonly _renderHeader = (): JSX.Element => {
        return (
            <div>
                {this._repoNames && this._repoNames.length === 1
                    ? Resources.FirstRunExperience_Header_ForSingleRepo
                    : Resources.FirstRunExperience_Header_ForMultiRepos}
            </div>
        );
    };

    private readonly _renderFooter = (): JSX.Element | undefined => {
        const { showInteractiveContent, isStepProcessing, stepErrorMessage, isCompleted } = this.state;
        return (
            <div className="footer-buttons-container align-right">
                {stepErrorMessage || isCompleted || !showInteractiveContent ? (
                    <>
                        <Button
                            className="footer-button close-button"
                            text={Resources.FirstRunExperience_FooterButton_Close}
                            onClick={this._onSkipOrCloseButtonClicked}
                        />
                        {isCompleted && (
                            <Button
                                className="footer-button view-work-item-button"
                                text={Resources.FirstRunExperience_FooterButton_ViewWorkItem}
                                primary={true}
                                onClick={this._onViewWorkItemButtonClicked}
                            />
                        )}
                    </>
                ) : (
                    <Button
                        className="footer-button skip-button"
                        disabled={isStepProcessing}
                        text={Resources.FirstRunExperience_FooterButton_Skip}
                        onClick={this._onSkipOrCloseButtonClicked}
                    />
                )}
            </div>
        );
    };

    private readonly _onSkipOrCloseButtonClicked = (): void => {
        this._onDismiss();
    };

    private readonly _onViewWorkItemButtonClicked = (): void => {
        this._onDismiss(true);
        this._openWorkItem();
    };

    private _openWorkItem(): void {
        try {
            const workItemService = this.context.pageContext.getService<IWorkItemService>(WorkItemServiceId);
            if (!workItemService) {
                throw new Error("Cannot create work item service");
            }
            workItemService.openWorkItem(this._targetWorkItem.id, {
                onRenderComplete: (workItem: any) => {
                    const gitHubLinkPrefix = "vstfs:///github";
                    const links = workItem ? workItem.getLinks() : [];
                    const gitHubLinksCount =
                        links instanceof Array
                            ? links.filter(
                                  l =>
                                      l &&
                                      l.linkData &&
                                      l.linkData.FilePath &&
                                      (l.linkData.FilePath as string).toLowerCase().indexOf(gitHubLinkPrefix) === 0
                              ).length
                            : 0;
                    publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_OPEN_WORKITEM, {
                        gitHubLinksCount
                    });
                }
            });
        } catch (error) {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_OPEN_WORKITEM, {
                error: error.message
            });
        }
    }

    private readonly _onDismiss = (willOpenWorkItem?: boolean) => {
        // Log telemetry to summarize panel status
        const { activeStepId, isCompleted, stepErrorMessage, showInteractiveContent } = this.state;
        if (showInteractiveContent) {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_PANEL_CLOSED, {
                isCompleted,
                activeStepId,
                willOpenWorkItem: willOpenWorkItem === true,
                totalElapsedTime: this._completeTimeStamp - this._startTimeStamp,
                createGitHubPullRequestStepElapsedTime: this._createGitHubPullRequestStepElapsedTime,
                timeStayedAfterCompletion: Date.now() - this._completeTimeStamp,
                stepErrorMessage
            });
        } else {
            publishGitHubIntegrationTelemetry(this.context, GitHubIntegrationCIActions.FRE_STATIC_PANEL_CLOSED);
        }

        // Close panel
        this.props.onDismiss();

        // Mark user setting to prevent it from showing in future
        this._markFirstRunExperienceClosedForUser();
    };

    private readonly _renderInteractiveContent = (): JSX.Element => {
        const { defaultWorkItemType } = this.props.boardInfo as IBoardInfo;
        return (
            <div className="interactive-content">
                <img className="panel-image" src={this._panelImageSource} />
                <div className="steps-header">
                    <span className="steps-header-sub-header title-xs">{Resources.FirstRunExperience_StepsHeader_SubHeader}</span>
                    <span className="steps-header-content body-m">
                        {localeFormat(Resources.FirstRunExperience_StepsHeader_Content, defaultWorkItemType)}
                    </span>
                </div>
                <div className="steps-container">{this._renderInteractiveSteps()}</div>
            </div>
        );
    };

    private _renderInteractiveSteps(): JSX.Element {
        const { projectId, boardInfo } = this.props;
        const { activeStepId, isCompleted, stepErrorMessage } = this.state;
        return (
            <>
                <FirstRunExperienceCreateWorkItemStep
                    stepId={1}
                    activeStepId={activeStepId}
                    projectId={projectId!}
                    boardInfo={boardInfo!}
                    onStepProcessing={this._onStepProcessing}
                    onStepSucceeded={this._onCreateWorkItemStepSucceeded}
                    onStepFailed={this._onCreateWorkItemStepFailed}
                />
                {activeStepId === 1 && stepErrorMessage ? (
                    // Hide the rest of steps if current one errors
                    undefined
                ) : (
                    <>
                        <FirstRunExperienceCreateGitHubPullRequestStep
                            stepId={2}
                            activeStepId={activeStepId}
                            projectId={projectId!}
                            boardInfo={boardInfo!}
                            repoNames={this._repoNames}
                            workItem={this._targetWorkItem}
                            onStepProcessing={this._onStepProcessing}
                            onStepSucceeded={this._onCreateGitHubPullRequestStepSucceeded}
                            onStepFailed={this._onCreateGitHubPullRequestStepFailed}
                        />
                        {isCompleted && (
                            <div className="quick-tip">
                                <span className="label title-xs">{Resources.FirstRunExperience_QuickTip_Label}</span>
                                <span className="content body-m">{Resources.FirstRunExperience_QuickTip_Content}</span>
                            </div>
                        )}
                    </>
                )}
            </>
        );
    }

    private readonly _onStepProcessing = (): void => {
        this.setState({ isStepProcessing: true });
    };

    private readonly _onCreateWorkItemStepSucceeded = (workItem: WorkItem): void => {
        this._targetWorkItem = workItem;
        this._moveToNextStep();
    };

    private readonly _onCreateWorkItemStepFailed = (errorMessage: string): void => {
        this._reportStepFailure(errorMessage);
    };

    private readonly _onCreateGitHubPullRequestStepSucceeded = (elapsedTime: number): void => {
        this._createGitHubPullRequestStepElapsedTime = elapsedTime;
        this._moveToNextStep();
    };

    private readonly _onCreateGitHubPullRequestStepFailed = (errorMessage: string, elapsedTime: number): void => {
        this._createGitHubPullRequestStepElapsedTime = elapsedTime;
        this._reportStepFailure(errorMessage);
    };

    private _moveToNextStep(): void {
        const { activeStepId } = this.state;
        const nextStepId = activeStepId + 1;
        this.setState(
            {
                activeStepId: nextStepId,
                isStepProcessing: false,
                isCompleted: nextStepId === TotalFirstRunStepsCount + 1
            },
            () => {
                if (this.state.isCompleted) {
                    this._completeTimeStamp = Date.now();
                }
            }
        );
    }

    private _reportStepFailure(errorMessage: string): void {
        this.setState({
            isStepProcessing: false,
            isCompleted: false,
            stepErrorMessage: errorMessage
        });
    }

    private _shouldShowInteractiveContent(): boolean {
        const { isInteractive, projectId, boardInfo } = this.props;
        return !!(
            isInteractive &&
            projectId &&
            boardInfo &&
            boardInfo.defaultWorkItemType &&
            boardInfo.workItemTeamFieldName &&
            boardInfo.workItemTeamFieldValue &&
            boardInfo.firstColumnName &&
            this._repoNames &&
            this._repoNames.length > 0
        );
    }

    private _markFirstRunExperienceClosedForUser(): void {
        const settingEntries: { [key: string]: any } = { [GitHubConnectionConstants.FirstRunExperienceClosedSettingKey]: true };
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries(settingEntries, SettingsUserScope.Me);
    }
}

export function showFirstRunExperiencePanel(
    pageContext: IVssPageContext,
    isInteractive: boolean,
    connection: ExternalConnection,
    projectId?: string,
    boardInfo?: IBoardInfo
): void {
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(closeCallout => {
        return (
            <FirstRunExperiencePanel
                connection={connection}
                projectId={projectId}
                boardInfo={boardInfo}
                isInteractive={isInteractive}
                onDismiss={closeCallout}
            />
        );
    });
}
